import { Spacer, TextAreaInput } from "@hackthenorth/north";
import {
  ArrowLeftIcon,
  MinusSmallIcon,
  PlusSmallIcon,
} from "@heroicons/react/24/outline";
import debounce from "lodash.debounce";
import moment from "moment";
import { Button, DropdownMenu, Text, TextInput } from "north.js";
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { QueryMode } from "src/api/types.generated";
import { PageWrapper } from "src/shared/components";
import Autocomplete from "src/shared/components/Autocomplete";
import { EVENT_ID } from "src/shared/constants/event";

import "twin.macro";
import {
  TPrimaryRoles,
  TSupplementaryRoles,
} from "../../shared/constants/roles";
import { convertDayToDate } from "../../shared/contexts/constants";
import { useEditShiftContext } from "../../shared/contexts/EditShiftContext";
import {
  getVolunteerShiftDayOfWeek,
  getVolunteerShiftHourAndMinute24Hour,
} from "../../shared/utils/date";
import { parseMaxCountRestriction } from "../../shift-schedule/utils";
import { useDeleteVolunteerShiftMutation } from "../graphql/deleteVolunteerShift.generated";
import { useGetShiftLeadsLazyQuery } from "../graphql/getShiftLeads.generated";
import { useVolunteerShiftsLazyQuery } from "../graphql/volunteerShifts.generated";

const EditVolunteerShiftPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const shiftId: number = useMemo(() => {
    return parseInt(searchParams.get("shift_id") ?? "");
  }, [searchParams]);

  const {
    shiftName,
    shiftDateStart,
    shiftDateEnd,
    timeStart,
    timeEnd,
    location,
    shiftLead,
    roles,
    supplementaryType,
    requiredVolunteers,
    minVolunteers,
    description,

    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateShiftLead,
    updateRoles,
    updateSupplementaryType,
    updateRequiredVolunteers,
    updateMinVolunteers,
    updateDescription,

    updateShift,
  } = useEditShiftContext();

  const [getVolunteerShift, volunteerShiftResult] =
    useVolunteerShiftsLazyQuery();
  // Separate from the shift query so searching never clobbers the loaded lead.
  const [searchShiftLeads, shiftLeadSearchResult] = useGetShiftLeadsLazyQuery();

  const debouncedSearchShiftLeads = useMemo(
    () =>
      debounce((searchValue: string) => {
        if (searchValue === "") return;
        searchShiftLeads({
          variables: {
            where: {
              OR: [
                {
                  event_attendees: {
                    some: {
                      event_id: { equals: EVENT_ID },
                      volunteer: { is: { is_lead: { equals: true } } },
                    },
                  },
                  name: {
                    contains: searchValue,
                    mode: QueryMode.Insensitive,
                  },
                },
                {
                  email: {
                    endsWith: "hackthenorth.com",
                    mode: QueryMode.Insensitive,
                  },
                  name: {
                    contains: searchValue,
                    mode: QueryMode.Insensitive,
                  },
                },
              ],
            },
            take: 20,
          },
        });
      }, 500),
    [searchShiftLeads]
  );

  const [deleteShift] = useDeleteVolunteerShiftMutation();

  // FETCH THE VOLUNTEER SHIFT DATA
  useEffect(() => {
    if (shiftId) {
      getVolunteerShift({
        variables: {
          where: {
            shift_id: {
              equals: shiftId,
            },
          },
        },
      });
    }
  }, [shiftId, getVolunteerShift]);

  const volunteerShiftData = useMemo(() => {
    if (volunteerShiftResult.data) {
      return volunteerShiftResult.data.volunteerShifts[0];
    }

    return null;
  }, [volunteerShiftResult.data]);

  const validTimeFrame = (
    startDate: string,
    endDate: string,
    startTime: string,
    endTime: string
  ) => {
    const startDateTime = moment(
      `${convertDayToDate(startDate)} ${startTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const endDateTime = moment(
      `${convertDayToDate(endDate)} ${endTime}`,
      "YYYY-MM-DD HH:mm"
    );

    return startDateTime.isBefore(endDateTime);
  };

  // SET INITIAL DATA: lead id + name come from the shift payload, so save is
  // never blocked waiting on a second organizer lookup.
  useEffect(() => {
    if (volunteerShiftData) {
      updateShiftName(volunteerShiftData.shift.name);
      updateShiftDateStart(
        getVolunteerShiftDayOfWeek(
          new Date(volunteerShiftData.shift.start_time)
        )
      );
      updateShiftDateEnd(
        getVolunteerShiftDayOfWeek(new Date(volunteerShiftData.shift.end_time))
      );
      updateTimeStart(
        getVolunteerShiftHourAndMinute24Hour(
          new Date(volunteerShiftData.shift.start_time)
        )
      );
      updateTimeEnd(
        getVolunteerShiftHourAndMinute24Hour(
          new Date(volunteerShiftData.shift.end_time)
        )
      );
      updateLocation(volunteerShiftData.shift.location ?? "");

      // Id + name are already on the shift; don't wait for a users query.
      updateShiftLead(
        volunteerShiftData.organizer_responsible?.name ?? "",
        volunteerShiftData.organizer_responsible_user_id ?? null
      );

      // Set roles
      if (!volunteerShiftData.category.primary_only) {
        updateRoles(TPrimaryRoles.SUPPLEMENTARY);
        updateSupplementaryType(volunteerShiftData.category.name);
      } else {
        updateRoles(volunteerShiftData.category.name);
      }
      updateMinVolunteers(volunteerShiftData.preferred_min_signups);
      updateRequiredVolunteers(
        parseMaxCountRestriction(volunteerShiftData.shift.restrictions)
      );
      updateDescription(volunteerShiftData.description);
    }
  }, [
    volunteerShiftData,
    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateShiftLead,
    updateRoles,
    updateMinVolunteers,
    updateRequiredVolunteers,
    updateDescription,
    updateSupplementaryType,
  ]);

  // Date selection choices
  const dateChoices = useMemo(() => {
    return (isStart: boolean) => [
      [
        {
          content: "Friday",
          action: (e: any) => {
            e.preventDefault();
            isStart
              ? updateShiftDateStart("Friday")
              : updateShiftDateEnd("Friday");
          },
        },
        {
          content: "Saturday",
          action: (e: any) => {
            e.preventDefault();
            isStart
              ? updateShiftDateStart("Saturday")
              : updateShiftDateEnd("Saturday");
          },
        },
        {
          content: "Sunday",
          action: (e: any) => {
            e.preventDefault();
            isStart
              ? updateShiftDateStart("Sunday")
              : updateShiftDateEnd("Sunday");
          },
        },
      ],
    ];
  }, [updateShiftDateStart, updateShiftDateEnd]);

  // Time Start and Time End selection choices
  const timeStartChoices = useMemo(() => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push({
          content: time,
          action: (e: any) => {
            e.preventDefault();
            if (
              validTimeFrame(shiftDateStart, shiftDateEnd, timeStart, timeEnd)
            )
              updateTimeStart(time);
          },
        });
      }
    }
    return [times];
  }, [shiftDateEnd, shiftDateStart, timeEnd, timeStart, updateTimeStart]);

  const timeEndChoices = useMemo(() => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push({
          content: time,
          action: (e: any) => {
            e.preventDefault();

            if (
              validTimeFrame(shiftDateStart, shiftDateEnd, timeStart, timeEnd)
            )
              updateTimeEnd(time);
          },
        });
      }
    }
    return [times];
  }, [shiftDateStart, shiftDateEnd, timeStart, timeEnd, updateTimeEnd]);

  // Roles selection choices
  const rolesChoices = useMemo(() => {
    return [
      Object.values(TPrimaryRoles).map((role) => {
        return {
          content: role,
          action: (e: any) => {
            e.preventDefault();
            updateRoles(role);
          },
        };
      }),
    ];
  }, [updateRoles]);

  const supplementaryRoleChoices = useMemo(() => {
    return [
      Object.values(TSupplementaryRoles).map((role) => {
        return {
          content: role,
          action: (e: any) => {
            e.preventDefault();
            updateSupplementaryType(role);
          },
        };
      }),
    ];
  }, [updateSupplementaryType]);

  // Editing the shift
  const handleEditItem = useCallback(() => {
    updateShift(shiftId)
      .then(() => navigate("/volunteer/shifts"))
      // Stay on the form so the edit can be retried; the failure is toasted.
      .catch(() => undefined);
  }, [updateShift, shiftId, navigate]);

  // Deleting the shift
  const handleDeleteShift = useCallback(async () => {
    try {
      await deleteShift({
        variables: {
          shiftId,
        },
      });

      navigate("/volunteer/shifts");
    } catch (err) {
      console.error(err);
    }
  }, [deleteShift, navigate, shiftId]);

  return (
    <PageWrapper>
      <EditVolunteerShiftContainer>
        <div tw="flex flex-row gap-4 items-center">
          <ArrowLeftIcon tw="h-16 font-bold text-text-secondary cursor-pointer" />
          <Text
            type="button"
            tw="font-bold text-text-secondary cursor-pointer"
            color="secondary"
            onClick={() => navigate("/volunteer/shifts")} // TODO: FIX
          >
            Back to Shifts
          </Text>
        </div>

        <Spacer height={24} />

        <Text type="h2">Edit a shift</Text>
        <Spacer height={8} />

        <Text type="body1">
          Edit, edit, or delete shifts by filling the form below.
        </Text>
        <Spacer height={24} />

        {/* FORM */}
        <StyledForm>
          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift name
            </Text>

            <TextInput
              type="text"
              placeholder="Enter a name..."
              tw="text-text-tertiary font-medium h-48"
              width={"700px"}
              onChange={(e) => updateShiftName(e.target.value)}
              value={shiftName}
            />
          </InputContainer>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Date Start
              </Text>

              <StyledDropdown tw="w-full! z-30" items={dateChoices(true)}>
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {shiftDateStart === "" ? "Enter date" : shiftDateStart}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </StyledDropdown>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Date End
              </Text>

              <StyledDropdown tw="w-full! z-30" items={dateChoices(false)}>
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {shiftDateEnd === "" ? "Enter date" : shiftDateEnd}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </StyledDropdown>
            </InputContainer>
          </div>

          <div tw="flex flex-row w-full gap-48 z-20">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time Start
              </Text>

              <DropdownMenu
                tw="w-full! max-h-196 overflow-auto"
                items={timeStartChoices}
              >
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {timeStart === "" ? "Enter time start" : timeStart}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </DropdownMenu>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time End
              </Text>

              <DropdownMenu
                tw="w-full! max-h-196 overflow-auto"
                items={timeEndChoices}
              >
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {timeEnd === "" ? "Enter time end" : timeEnd}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </DropdownMenu>
            </InputContainer>
          </div>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Location
            </Text>

            <TextInput
              type="text"
              placeholder="Enter location"
              tw="text-text-tertiary font-medium h-48 w-full!"
              width={"700px"}
              value={location}
              onChange={(e) => updateLocation(e.target.value)}
            />
          </InputContainer>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift Lead
            </Text>

            <Autocomplete
              tw="text-text-secondary font-medium h-48 w-full!"
              defaultValue={shiftLead}
              options={
                shiftLeadSearchResult.data?.users.map((user) => ({
                  label: `${user.name} (${user.email})`,
                  // Identify the lead by id as names are not unique.
                  value: user.id,
                })) || []
              }
              loading={shiftLeadSearchResult.loading}
              handleSelect={(value) => {
                const selected = shiftLeadSearchResult.data?.users.find(
                  (user) => user.id === Number(value)
                );
                updateShiftLead(selected?.name ?? "", selected?.id ?? null);
              }}
              handleTextInputChange={(value) => {
                if (value === "") {
                  // Drop any in-flight search so it can't land after the reset.
                  debouncedSearchShiftLeads.cancel();
                } else {
                  debouncedSearchShiftLeads(value);
                }
                // Typing invalidates the previous pick until a new one is made.
                if (shiftLead) {
                  updateShiftLead("", null);
                }
              }}
              renderInput={({ value, onChange }) => (
                <TextInput
                  type="text"
                  placeholder="Enter name"
                  tw="text-text-tertiary font-medium h-48 w-full!"
                  width={"700px"}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </InputContainer>

          <InputContainer tw="z-20">
            <Text type="body1" tw="font-bold">
              Roles
            </Text>

            <DropdownMenu tw="w-full!" items={rolesChoices}>
              <Button color="secondary" tw="w-full! h-48">
                <div tw="flex justify-between w-full!">
                  <Text type="body1" tw="text-text-tertiary font-medium">
                    {roles === "" ? "Enter roles" : roles}
                  </Text>
                  <ChevronDown />
                </div>
              </Button>
            </DropdownMenu>
          </InputContainer>

          {roles === "Supplementary" && (
            <InputContainer tw="z-10">
              <Text type="body1" tw="font-bold">
                Supplementary Type
              </Text>

              <DropdownMenu tw="w-full!" items={supplementaryRoleChoices}>
                <Button color="secondary" tw="w-full! h-48">
                  <div tw="flex justify-between w-full">
                    <Text type="body1" tw="text-text-tertiary font-medium">
                      {supplementaryType === ""
                        ? "Enter supplementary type"
                        : supplementaryType}
                    </Text>
                    <ChevronDown />
                  </div>
                </Button>
              </DropdownMenu>
            </InputContainer>
          )}

          <div tw="flex flex-row w-full gap-48">
            <InputContainer>
              <Text type="body1" tw="font-bold">
                Preferred Min Volunteers
              </Text>

              <div tw="flex flex-row gap-12 items-center">
                <MinusSmallIcon
                  tw="cursor-pointer"
                  onClick={(e) => {
                    if (minVolunteers - 1 >= 0) {
                      updateMinVolunteers(minVolunteers - 1);
                    }
                  }}
                  height={24}
                />
                <div>
                  <TextInput
                    tw="text-text-secondary font-medium text-center h-48 w-48"
                    value={minVolunteers}
                  />
                </div>

                <PlusSmallIcon
                  tw="cursor-pointer"
                  onClick={(e) => {
                    if (minVolunteers + 1 <= requiredVolunteers) {
                      updateMinVolunteers(minVolunteers + 1);
                    }
                  }}
                  height={24}
                />
              </div>
            </InputContainer>

            <InputContainer>
              <Text type="body1" tw="font-bold">
                Preferred Max Volunteers
              </Text>

              <div tw="flex flex-row gap-12 items-center">
                <MinusSmallIcon
                  tw="cursor-pointer"
                  onClick={(e) => {
                    if (
                      requiredVolunteers - 1 >= 0 &&
                      requiredVolunteers > minVolunteers
                    ) {
                      updateRequiredVolunteers(requiredVolunteers - 1);
                    }
                  }}
                  height={24}
                />
                <div>
                  <TextInput
                    tw="text-text-secondary font-medium text-center h-48 w-48"
                    value={requiredVolunteers}
                  />
                </div>

                <PlusSmallIcon
                  tw="cursor-pointer"
                  onClick={(e) =>
                    updateRequiredVolunteers(requiredVolunteers + 1)
                  }
                  height={24}
                />
              </div>
            </InputContainer>
          </div>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift Description
            </Text>

            <TextAreaInput
              placeholder="Enter description..."
              tw="text-text-tertiary font-medium w-full!"
              value={description}
              fullWidth
              maxChars={500}
              onChange={(e: any) => updateDescription(e.target.value)}
            />
          </InputContainer>
        </StyledForm>

        <Spacer height={64} />

        <div tw="flex flex-row gap-16">
          <Button color="secondary-danger">
            <Text type="button" onClick={handleDeleteShift}>
              Delete Shift
            </Text>
          </Button>

          <Button color="primary">
            <Text type="button" onClick={handleEditItem}>
              Save Item
            </Text>
          </Button>
        </div>
      </EditVolunteerShiftContainer>
    </PageWrapper>
  );
};

const ChevronDown = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-6 h-6"
    tw="text-text-tertiary w-12"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </svg>
);

const EditVolunteerShiftContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  max-width: 700px;
`;

const StyledDropdown = styled(DropdownMenu)`
  z-index: 1;
`;

export default EditVolunteerShiftPage;
